import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import { importHotel } from '../../services/hotelService';

interface ImportModalProps {
  open: boolean;
  onClose: () => void;
  hotel: any;
  onSuccess: (importedHotel: any) => void;
}

const ImportModal: React.FC<ImportModalProps> = ({
  open,
  onClose,
  hotel,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleImport = async () => {
    if (!hotel) return;

    setLoading(true);
    setError(null);

    try {
      const importedHotel = await importHotel({
        source: hotel.source,
        sourceId: hotel.sourceId,
      });
      onSuccess(importedHotel);
    } catch (err) {
      console.error('Error importing hotel:', err);
      setError('Failed to import hotel. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!hotel) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Import Hotel</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography variant="h6" gutterBottom>
          {hotel.title}
        </Typography>

        <Box mb={2}>
          <Typography variant="body2" color="textSecondary">
            Location: {hotel.location}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Price: {hotel.price}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" gutterBottom>
          Description
        </Typography>
        <Typography variant="body2" paragraph>
          {hotel.description}
        </Typography>

        <Typography variant="subtitle2" gutterBottom>
          Features
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
          {hotel.features.map((feature: string, index: number) => (
            <Typography
              key={index}
              variant="body2"
              color="textSecondary"
              component="span"
            >
              • {feature}
            </Typography>
          ))}
        </Box>

        <Alert severity="info" sx={{ mt: 2 }}>
          This will import the hotel data into your account. You can edit the details
          after import.
        </Alert>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleImport}
          variant="contained"
          color="primary"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Importing...' : 'Import Hotel'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportModal;